import React, { useState, useEffect } from 'react';

const PDFExportModal = ({ isOpen, onClose, hazardId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);

  const steps = [
    { label: 'Initializing', duration: 800 },
    { label: 'Parsing incident data', duration: 1200 },
    { label: 'Analyzing with AI', duration: 1500 },
    { label: 'Generating report structure', duration: 1000 },
    { label: 'Formatting document', duration: 1200 },
    { label: 'Adding branding elements', duration: 800 },
    { label: 'Finalizing PDF', duration: 1000 }
  ];

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      setProgress(0);
      setIsMinimized(false);
      return;
    }

    // Simulate progress through steps
    let stepIndex = 0;
    let progressValue = 0;

    const totalDuration = steps.reduce((sum, step) => sum + step.duration, 0);
    const progressInterval = setInterval(() => {
      progressValue += 100 / (totalDuration / 50); // Update every 50ms
      if (progressValue >= 100) {
        progressValue = 100;
        clearInterval(progressInterval);
      }
      setProgress(progressValue);
    }, 50);

    const stepTimer = () => {
      if (stepIndex < steps.length) {
        setCurrentStep(stepIndex);
        stepIndex++;
        setTimeout(stepTimer, steps[stepIndex - 1]?.duration || 0);
      }
    };

    stepTimer();

    return () => {
      clearInterval(progressInterval);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Minimized view
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-2xl border-2 border-[#1B5C94] w-80 z-50 animate-slide-up">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#1B5C94]"></div>
              <span className="text-sm font-semibold text-gray-800">Exporting PDF...</span>
            </div>
            <button
              onClick={() => setIsMinimized(false)}
              className="text-[#1B5C94] hover:text-[#3AC0DB] font-bold text-lg"
              title="Maximize"
            >
              ▲
            </button>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] h-2.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-600 mt-2 text-center">{Math.round(progress)}%</p>
        </div>
      </div>
    );
  }

  // Full modal view
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in-scale">
        {/* Header with Saarv Branding */}
        <div className="bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] rounded-t-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white rounded-full p-2">
                <svg className="w-6 h-6 text-[#1B5C94]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold">Generating PDF Report</h3>
                <p className="text-sm text-white/80">SAARV Safety Management</p>
              </div>
            </div>
            <button
              onClick={() => setIsMinimized(true)}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              title="Minimize"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Step Indicator */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#1B5C94] to-[#3AC0DB] rounded-full mb-4 animate-pulse-slow">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-gray-800 mb-2">
              {steps[currentStep]?.label || 'Processing...'}
            </h4>
            <p className="text-sm text-gray-600">
              Step {currentStep + 1} of {steps.length}
            </p>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
              <div
                className="bg-gradient-to-r from-[#1B5C94] via-[#3AC0DB] to-[#1B5C94] h-3 rounded-full transition-all duration-300 ease-out animate-gradient-x"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">{Math.round(progress)}%</span>
              <span className="text-gray-500">Estimated: {Math.ceil((100 - progress) / 12)}s</span>
            </div>
          </div>

          {/* Step List */}
          <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
            <div className="space-y-2">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center space-x-3 transition-all duration-300 ${
                    index < currentStep
                      ? 'opacity-60'
                      : index === currentStep
                      ? 'opacity-100 transform scale-105'
                      : 'opacity-40'
                  }`}
                >
                  {index < currentStep ? (
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ) : index === currentStep ? (
                    <div className="w-5 h-5 rounded-full border-2 border-[#1B5C94] flex items-center justify-center flex-shrink-0 animate-pulse">
                      <div className="w-2 h-2 rounded-full bg-[#1B5C94]"></div>
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0"></div>
                  )}
                  <span
                    className={`text-sm ${
                      index === currentStep
                        ? 'font-semibold text-[#1B5C94]'
                        : index < currentStep
                        ? 'text-gray-600 line-through'
                        : 'text-gray-400'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Info Message */}
          <div className="flex items-start space-x-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="text-xs text-blue-800">
              Your PDF report is being generated with AI-powered analysis and professional formatting. This may take a few moments.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFExportModal;
